import {
  addWorksheetItem,
  getWorksheetItems,
  isWorksheetPendingPartsError,
  worksheetBillErrorToastKey,
  type WorksheetItem,
  type WorksheetItemType,
} from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { WorksheetPrintPreview } from '@/components/worksheet/WorksheetPrintPreview';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { usePartner } from '@/hooks/use-partners';
import { useProcurementRequests } from '@/hooks/use-procurement-requests';
import { useWorksheetStatusHistory } from '@/hooks/use-worksheet-status-history';
import { useWorksheet, useWorksheetMutations } from '@/hooks/use-worksheets';
import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isPendingProcurement } from './components/CloseWorksheetConfirmDialog';
import { CostAllocationSection } from './components/CostAllocationSection';
import { DisassemblyModal } from './components/DisassemblyModal';
import { ExternalForwardSection } from './components/ExternalForwardSection';
import { PaymentGateBanner, shouldShowPaymentGate } from './components/PaymentGateBanner';
import { ProcurementRequestSection } from './components/ProcurementRequestSection';
import { TechnicianAssignmentSection } from './components/TechnicianAssignmentSection';
import { WarrantyClaimSection } from './components/WarrantyClaimSection';
import { WorksheetBatchPanel } from './components/WorksheetBatchPanel';
import {
  buildStatusHistoryEvents,
  buildWorksheetHistoryEvents,
  WorksheetHistoryTimeline,
} from './components/WorksheetHistoryTimeline';
import { WorksheetPhotoGallery } from './components/WorksheetPhotoGallery';
import { WorksheetStateStepper } from './components/WorksheetStateStepper';
import {
  PRIORITY_LABEL_KEYS,
  STATUS_LABEL_KEYS,
  TYPE_LABEL_KEYS,
  WorksheetStatus,
  WorksheetType,
} from './types';

// Utility functions
const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('hu-HU');
};

const formatDateTime = (dateStr: string): string => {
  return new Date(dateStr).toLocaleString('hu-HU');
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
};

type TabKey = 'overview' | 'diagnosis' | 'parts' | 'history' | 'photos' | 'warranty';

/**
 * QA-103: a munkalap fejlécében partner-NÉV a nyers UUID helyett.
 *
 * A három állapot SZÁNDÉKOSAN megkülönböztethető (🎬 kérése): a kliens-lépéssor
 * „keresd ki a partnert a nevére" viselkedést ígér, ezért a betöltés és a
 * fel-nem-oldható azonosító nem eshet egybe — különben a hiba „lassúságnak"
 * olvasódik, és a felhasználó nem szól.
 */
function WorksheetPartnerLabel({ partnerId }: { partnerId: string }) {
  const { t } = useTranslation();
  const { partner, isLoading } = usePartner(partnerId);

  if (isLoading) {
    return (
      <p
        className="text-sm text-gray-500 dark:text-gray-400"
        data-testid="worksheet-partner-loading"
      >
        {t('worksheet.partner.loading')}
      </p>
    );
  }

  if (partner?.name) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="worksheet-partner-name">
        {t('worksheet.partner.label', { name: partner.name })}
      </p>
    );
  }

  // Fail-visible fallback: a felület nem lesz rosszabb a mainál (az azonosító
  // megmarad), de a név hiánya MEGJELÖLT, nem néma.
  return (
    <p
      className="text-sm text-gray-500 dark:text-gray-400"
      data-testid="worksheet-partner-unresolved"
    >
      {t('worksheet.partner.unresolved', { id: partnerId })}
    </p>
  );
}

export function WorksheetDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { worksheet, isLoading, error, refetch } = useWorksheet(id);
  const {
    changeStatus,
    billWorksheet: billWorksheetMutation,
    isLoading: isMutating,
  } = useWorksheetMutations();
  // Q-126-01: pending procurement (DRAFT/SUBMITTED/ORDERED) for the close-warning.
  // `id` (route param) is available immediately, so the query is always scoped.
  const { requests: procurementRequests } = useProcurementRequests({ worksheetId: id });
  const pendingProcurement = procurementRequests.filter(isPendingProcurement);
  // PV-SZERVIZ-002: a munkalap befejezése/lezárása TILTOTT függő alkatrész-beszerzéssel.
  const hasPendingParts = pendingProcurement.length > 0;
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  // SRV-FG-5-1: Disassembly modal state
  const [showDisassembly, setShowDisassembly] = useState(false);

  // N-SRV-3: Worksheet items state
  const [items, setItems] = useState<WorksheetItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showExternalCostForm, setShowExternalCostForm] = useState(false);
  const [externalCostDesc, setExternalCostDesc] = useState('');
  const [externalCostAmount, setExternalCostAmount] = useState('');
  const [externalCostError, setExternalCostError] = useState<string | null>(null);
  const [externalCostSaving, setExternalCostSaving] = useState(false);

  // Feature flag + tab state (flag OFF by default — new layout opt-in only)
  const detailTabsEnabled = useFeatureFlag('WORKSHEET_DETAIL_TABS');
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  // #433/#438: státusz-történet lazy-fetch — csak az aktív history-tabon
  const { entries: statusHistoryEntries } = useWorksheetStatusHistory(
    activeTab === 'history' ? worksheet?.id : undefined
  );

  const fetchItems = useCallback(async () => {
    if (!id) return;
    setItemsLoading(true);
    try {
      const response = await getWorksheetItems(id);
      setItems(response.data);
    } catch {
      // Items loading failure is non-critical
    } finally {
      setItemsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void fetchItems();
    }
  }, [id, fetchItems]);

  const handleAddExternalCost = async () => {
    setExternalCostError(null);

    if (!externalCostDesc.trim() || externalCostDesc.trim().length < 2) {
      setExternalCostError(t('service.externalCostDescriptionRequired'));
      return;
    }
    const amount = Number(externalCostAmount);
    if (!amount || amount <= 0) {
      setExternalCostError(t('service.externalCostAmountPositive'));
      return;
    }

    if (!id) return;

    setExternalCostSaving(true);
    try {
      await addWorksheetItem(id, {
        itemType: 'EXTERNAL_COST',
        description: externalCostDesc.trim(),
        unitPrice: amount,
        quantity: 1,
      });
      setExternalCostDesc('');
      setExternalCostAmount('');
      setShowExternalCostForm(false);
      await fetchItems();
    } catch (err) {
      setExternalCostError(err instanceof Error ? err.message : 'Error');
    } finally {
      setExternalCostSaving(false);
    }
  };

  const getItemTypeLabelKey = (itemType: WorksheetItemType): string => {
    switch (itemType) {
      case 'PART':
        return 'service.itemTypePart';
      case 'LABOR':
        return 'service.itemTypeLabor';
      case 'OTHER':
        return 'service.itemTypeOther';
      case 'EXTERNAL_COST':
        return 'service.itemTypeExternalCost';
      default:
        return 'service.itemTypeOther';
    }
  };

  const getItemTypeBadgeColor = (itemType: WorksheetItemType): string => {
    switch (itemType) {
      case 'PART':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 'LABOR':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'EXTERNAL_COST':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Auto-open print preview when navigated from wizard with ?print=true
  useEffect(() => {
    if (searchParams.get('print') === 'true' && worksheet && !isLoading) {
      setShowPrintPreview(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, worksheet, isLoading, setSearchParams]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen kgc-bg p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">{t('service.worksheetLoading')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen kgc-bg p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-red-500 mb-4">{t('page.errorPrefix', { message: error })}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => refetch()}>{t('common.retry')}</Button>
                <Button variant="outline" onClick={() => navigate('/worksheet')}>
                  {t('common.backToList')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Not found state
  if (!worksheet) {
    return (
      <div className="min-h-screen kgc-bg p-6">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {t('service.worksheetNotFound')}
              </p>
              <Button onClick={() => navigate('/worksheet')}>{t('common.backToList')}</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleStatusChange = async (newStatus: WorksheetStatus) => {
    try {
      await changeStatus(worksheet.id, newStatus);
      refetch();
    } catch (err) {
      // PV-SZERVIZ-002 defense-in-depth: a BE-422 WORKSHEET_PENDING_PARTS → specifikus toast.
      if (isWorksheetPendingPartsError(err)) {
        toast.error(t('worksheet.pendingPartsBlock.toast'));
      } else {
        console.error('Status change failed:', err);
      }
    }
  };

  // 34-kör R-1: a KESZ-munkalap számlázása (POST /worksheets/:id/bill).
  // A régi gomb státusz-PATCH-csel (SZAMLAZANDO) próbálkozott — az NEM generál számlát
  // és a derived-státusz miatt inkonzisztens; ez a billing-endpoint a SSoT.
  // Siker: a wire-státusz KESZ→SZAMLAZANDO derived-en vált (invoiceId beáll → refetch).
  const handleBill = async () => {
    try {
      await billWorksheetMutation(worksheet.id, { paymentMethod: 'CASH' });
      toast.success(t('service.invoiceSuccess'));
      refetch();
    } catch (err) {
      toast.error(t(worksheetBillErrorToastKey(err)));
    }
  };

  const getStatusColor = (status: WorksheetStatus) => {
    switch (status) {
      case WorksheetStatus.FELVEVE:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case WorksheetStatus.FOLYAMATBAN:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
      case WorksheetStatus.VARHATO:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      case WorksheetStatus.FELFUGGESZTVE:
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/50 dark:text-orange-400';
      case WorksheetStatus.KESZ:
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case WorksheetStatus.SZAMLAZANDO:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case WorksheetStatus.LEZART:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case WorksheetStatus.TOROLVE:
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // ── Fragment extractions ─────────────────────────────────────────────────
  // Each const captures the JSX verbatim from the original render.
  // All state/handlers are in component scope — fragments reference them directly.

  const equipmentCard = (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.equipmentProduct')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.designation')}</p>
            <p className="font-medium">{worksheet.deviceName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.serialNumber')}</p>
            <p className="font-medium font-mono">{worksheet.deviceSerialNumber || '-'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const faultCard = (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.faultDescription')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
          {worksheet.faultDescription}
        </p>
      </CardContent>
    </Card>
  );

  const diagnosticsCtaCard =
    worksheet.status === 'FELVEVE' && !worksheet.diagnosis ? (
      <Card className="border-primary/50 bg-primary/5 dark:bg-primary/10">
        <CardContent className="p-6 text-center">
          <svg
            className="h-10 w-10 mx-auto mb-3 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <h3 className="text-lg font-semibold text-primary mb-1">
            {t('service.diagnosticsNeeded')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-[#ccfbf1] mb-4">
            {t('service.diagnosticsNeededDesc')}
          </p>
          <Button
            onClick={() => navigate(`/worksheet/${worksheet.id}/edit`)}
            className="bg-primary hover:bg-primary/90"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            {t('service.startDiagnostics')}
          </Button>
        </CardContent>
      </Card>
    ) : null;

  const diagnosisWorkCard =
    worksheet.diagnosis || worksheet.workPerformed ? (
      <Card>
        <CardHeader>
          <CardTitle>{t('service.diagnosisAndWork')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {worksheet.diagnosis && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t('service.diagnosis')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {worksheet.diagnosis}
              </p>
            </div>
          )}
          {worksheet.workPerformed && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {t('service.completedWork')}
              </p>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {worksheet.workPerformed}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    ) : null;

  const itemsCard = (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('service.worksheetItems')}</CardTitle>
          {!['LEZART', 'TOROLVE', 'SZAMLAZANDO'].includes(worksheet.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExternalCostForm(!showExternalCostForm)}
            >
              {t('service.addExternalCost')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Add External Cost Form */}
        {showExternalCostForm && (
          <div className="mb-4 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4">
            <h4 className="text-sm font-medium mb-3">{t('service.addExternalCost')}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('service.externalCostDescription')}
                </label>
                <Input
                  value={externalCostDesc}
                  onChange={e => setExternalCostDesc(e.target.value)}
                  placeholder={t('service.externalCostDescriptionPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {t('service.externalCostAmount')}
                </label>
                <Input
                  type="number"
                  min={1}
                  value={externalCostAmount}
                  onChange={e => setExternalCostAmount(e.target.value)}
                  placeholder={t('service.externalCostAmountPlaceholder')}
                />
              </div>
            </div>
            {externalCostError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-2">{externalCostError}</p>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddExternalCost} disabled={externalCostSaving}>
                {externalCostSaving ? t('common.saving') : t('common.add')}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowExternalCostForm(false);
                  setExternalCostError(null);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}

        {/* Items List */}
        {itemsLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.noItems')}</p>
        ) : (
          <div className="space-y-2">
            {items.map(item => (
              <div
                key={item.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${getItemTypeBadgeColor(item.itemType)}`}
                  >
                    {t(getItemTypeLabelKey(item.itemType))}
                  </span>
                  <span className="text-sm text-gray-900 dark:text-white">{item.description}</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatCurrency(Number(item.totalPrice))}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const internalNoteCard = worksheet.internalNote ? (
    <Card className="border-yellow-300 dark:border-yellow-700">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <svg
            className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <CardTitle className="text-lg">{t('service.internalNote')}</CardTitle>
          <span className="rounded-full bg-yellow-100 dark:bg-yellow-900/50 px-2 py-0.5 text-xs font-medium text-yellow-700 dark:text-yellow-300">
            {t('service.internalOnly')}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30 p-4">
          <p className="text-yellow-800 dark:text-yellow-200 whitespace-pre-wrap">
            {worksheet.internalNote}
          </p>
        </div>
        <p className="mt-2 text-xs text-yellow-600 dark:text-yellow-500 flex items-center gap-1">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          {t('service.internalNoteHelper')}
        </p>
      </CardContent>
    </Card>
  ) : null;

  // SRV-FG-3: Photo Gallery
  const photoGallery = (
    <WorksheetPhotoGallery
      worksheetId={worksheet.id}
      isEditable={!['LEZART', 'TOROLVE', 'SZAMLAZANDO'].includes(worksheet.status)}
    />
  );

  // SRV-FG-3: Warranty Claim Section (only for GARANCIALIS type)
  const warrantyCard =
    worksheet.type === WorksheetType.GARANCIALIS ? (
      <WarrantyClaimSection worksheetId={worksheet.id} deviceName={worksheet.deviceName} />
    ) : null;

  // SRV-FG-5-9: Technician Assignment with max 2 co-tech guard
  const technicianCard = (
    <TechnicianAssignmentSection
      worksheetId={worksheet.id}
      isEditable={!['LEZART', 'TOROLVE', 'SZAMLAZANDO'].includes(worksheet.status)}
    />
  );

  // SRV-F1-5: Procurement Requests
  const procurementCard = (
    <ProcurementRequestSection
      worksheetId={worksheet.id}
      isEditable={!['LEZART', 'TOROLVE'].includes(worksheet.status)}
    />
  );

  // SRV-F2-4: Cost Allocation
  const costAllocationCard = <CostAllocationSection worksheetId={worksheet.id} />;

  const worksheetDataCard = (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.worksheetData')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.type')}</p>
          <p className="font-medium">
            {t(TYPE_LABEL_KEYS[worksheet.type as keyof typeof TYPE_LABEL_KEYS]) || worksheet.type}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.priority')}</p>
          <p className="font-medium">
            {t(PRIORITY_LABEL_KEYS[worksheet.priority as keyof typeof PRIORITY_LABEL_KEYS]) ||
              worksheet.priority}
          </p>
        </div>
        {worksheet.costLimit && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.costLimit')}</p>
            <p className="font-medium">{formatCurrency(worksheet.costLimit)}</p>
          </div>
        )}
        {worksheet.costLimit != null && worksheet.requiresQuote === false && (
          <div>
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/50 dark:text-green-300">
              {t('worksheet.directRepairBadge')}
            </span>
          </div>
        )}
        {worksheet.assignedToId && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.responsibleId')}</p>
            <p className="font-medium text-sm font-mono">{worksheet.assignedToId}</p>
          </div>
        )}
        {worksheet.estimatedCompletionDate && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('service.expectedCompletion')}
            </p>
            <p className="font-medium">{formatDate(worksheet.estimatedCompletionDate)}</p>
          </div>
        )}
        {worksheet.queuePosition !== undefined && worksheet.queuePosition !== null && (
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('service.queuePosition')}</p>
            <p className="font-medium">
              {t('service.queuePositionLabel', { position: worksheet.queuePosition })}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const storageCard =
    worksheet.storageDailyFee || worksheet.storageTotalFee ? (
      <Card>
        <CardHeader>
          <CardTitle>{t('service.storageFee')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {worksheet.storageStartDate && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('service.storageStart')}</span>
              <span className="font-medium">{formatDate(worksheet.storageStartDate)}</span>
            </div>
          )}
          {worksheet.storageDailyFee && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('rental.dailyRate')}</span>
              <span className="font-medium">{formatCurrency(worksheet.storageDailyFee)}</span>
            </div>
          )}
          {worksheet.storageTotalFee && (
            <div className="flex justify-between border-t pt-2">
              <span className="font-medium">{t('service.storageTotalFee')}</span>
              <span className="font-bold">{formatCurrency(worksheet.storageTotalFee)}</span>
            </div>
          )}
        </CardContent>
      </Card>
    ) : null;

  const timestampsCard = (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.timestamps')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('service.received')}</span>
          <span>{formatDateTime(worksheet.receivedAt)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('common.createdAt')}</span>
          <span>{formatDateTime(worksheet.createdAt)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('common.updatedAt')}</span>
          <span>{formatDateTime(worksheet.updatedAt)}</span>
        </div>
        {worksheet.completedAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('service.completed')}</span>
            <span className="text-green-600">{formatDateTime(worksheet.completedAt)}</span>
          </div>
        )}
        <div className="flex justify-between pt-2 border-t">
          <span className="text-muted-foreground">{t('service.creatorId')}</span>
          <span className="font-mono text-xs">{worksheet.createdBy}</span>
        </div>
      </CardContent>
    </Card>
  );

  const rentalLinkCard = worksheet.rentalId ? (
    <Card>
      <CardHeader>
        <CardTitle>{t('service.relatedRental')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          className="w-full"
          onClick={() => navigate(`/rental/${worksheet.rentalId}`)}
        >
          {t('service.viewRental')}
        </Button>
      </CardContent>
    </Card>
  ) : null;

  // ── Tab definitions ───────────────────────────────────────────────────────
  const TABS: { key: TabKey; labelKey: string }[] = [
    { key: 'overview', labelKey: 'worksheet.tabs.overview' },
    { key: 'diagnosis', labelKey: 'worksheet.tabs.diagnosis' },
    { key: 'parts', labelKey: 'worksheet.tabs.parts' },
    { key: 'history', labelKey: 'worksheet.tabs.history' },
    { key: 'photos', labelKey: 'worksheet.tabs.photos' },
    { key: 'warranty', labelKey: 'worksheet.tabs.warranty' },
  ];

  // ── Tabbed content area (flag ON) ─────────────────────────────────────────
  const tabbedLayout = (
    <div>
      {/* Tab navigation */}
      <nav
        role="tablist"
        className="flex border-b border-gray-200 dark:border-gray-700 mb-6 overflow-x-auto"
      >
        {TABS.map(tab => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            data-testid={`worksheet-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors focus:outline-none ${
              activeTab === tab.key
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </nav>

      {/* 2-column: main panel + persistent cost-summary rail */}
      <div className="flex gap-6 items-start">
        {/* Main tab panel */}
        <div className="flex-1 min-w-0 space-y-6">
          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-overview"
            style={{ display: activeTab === 'overview' ? undefined : 'none' }}
            className="space-y-6"
          >
            {equipmentCard}
            <WorksheetBatchPanel
              currentWorksheetId={worksheet.id}
              intakeBatchId={worksheet.intakeBatchId}
            />
            {faultCard}
            {diagnosticsCtaCard}
            {internalNoteCard}
            {worksheetDataCard}
            {storageCard}
            {rentalLinkCard}
          </div>

          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-diagnosis"
            style={{ display: activeTab === 'diagnosis' ? undefined : 'none' }}
            className="space-y-6"
          >
            {diagnosisWorkCard}
            {technicianCard}
          </div>

          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-parts"
            style={{ display: activeTab === 'parts' ? undefined : 'none' }}
            className="space-y-6"
          >
            {itemsCard}
            {procurementCard}
          </div>

          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-history"
            style={{ display: activeTab === 'history' ? undefined : 'none' }}
            className="space-y-6"
          >
            {/* page11 v3-parity: derived esemény-timeline (contract-append mezők opcionálisak) */}
            <Card>
              <CardHeader>
                <CardTitle>{t('worksheet.tabs.history')}</CardTitle>
              </CardHeader>
              <CardContent>
                <WorksheetHistoryTimeline
                  events={
                    statusHistoryEntries.length > 0
                      ? buildStatusHistoryEvents(statusHistoryEntries, t)
                      : buildWorksheetHistoryEvents(worksheet, t)
                  }
                />
              </CardContent>
            </Card>
            {timestampsCard}
          </div>

          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-photos"
            style={{ display: activeTab === 'photos' ? undefined : 'none' }}
            className="space-y-6"
          >
            {photoGallery}
          </div>

          <div
            role="tabpanel"
            data-testid="worksheet-tabpanel-warranty"
            style={{ display: activeTab === 'warranty' ? undefined : 'none' }}
            className="space-y-6"
          >
            {worksheet.type === WorksheetType.GARANCIALIS ? (
              warrantyCard
            ) : (
              <p className="text-sm text-muted-foreground">{t('worksheet.tabs.noWarranty')}</p>
            )}
          </div>
        </div>

        {/* Persistent cost-summary rail */}
        <div data-testid="worksheet-cost-rail" className="lg:w-80 shrink-0 space-y-6">
          {costAllocationCard}
        </div>
      </div>
    </div>
  );

  // ── Existing grid layout (flag OFF — byte-identical to original) ──────────
  const legacyGrid = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main info */}
      <div className="lg:col-span-2 space-y-6">
        {equipmentCard}
        {faultCard}
        {diagnosticsCtaCard}
        {diagnosisWorkCard}
        {itemsCard}
        {internalNoteCard}
        {photoGallery}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {warrantyCard}
        {technicianCard}
        {procurementCard}
        {costAllocationCard}
        {worksheetDataCard}
        {storageCard}
        {timestampsCard}
        {rentalLinkCard}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <div className="bg-white dark:bg-[#1a4f5c] border-b border-gray-200 dark:border-[#3a9aa8] px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/worksheet')}>
                {t('common.back')}
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {worksheet.worksheetNumber}
                </h1>
                <WorksheetPartnerLabel partnerId={worksheet.partnerId} />
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(worksheet.status as WorksheetStatus)}`}
              >
                {t(STATUS_LABEL_KEYS[worksheet.status as WorksheetStatus]) || worksheet.status}
              </span>
              {/* 34-kör R-1: SZAMLAZANDO (számlázva) → kattintható számla-link.
                  Csak ha invoiceId beállt (derived-státusz forrása). */}
              {worksheet.status === 'SZAMLAZANDO' && worksheet.invoiceId && (
                <button
                  type="button"
                  data-testid="worksheet-invoice-link"
                  onClick={() => navigate(`/invoices/${worksheet.invoiceId}`)}
                  className="text-sm font-medium text-primary underline-offset-2 hover:underline"
                >
                  {t('worksheet.viewInvoice')}
                </button>
              )}
              {worksheet.isWarranty && (
                <span
                  data-testid="warranty-badge"
                  className="px-3 py-1 rounded-full text-sm font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300"
                >
                  {t('worksheet.isWarranty')}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {/* 34-kör R-1: KESZ → „Számla kiállítása" (billing-endpoint, NEM státusz-PATCH).
                  Sikeres billing után invoiceId beáll → wire-státusz KESZ→SZAMLAZANDO derived.
                  Garanciális (type==='GARANCIALIS') KIZÁRVA: SOHA ügyfél-számlázható (a gyártó fedezi
                  CREDIT_NOTE-tal) → a CTA mindig 400 lenne; a 400 WARRANTY_NOT_BILLABLE backstop a toast-ban. */}
              {worksheet.status === 'KESZ' && worksheet.type !== 'GARANCIALIS' && (
                <Button
                  data-testid="worksheet-bill-cta"
                  onClick={() => void handleBill()}
                  disabled={isMutating}
                >
                  {t('service.issueInvoice')}
                </Button>
              )}
              {worksheet.status === 'FELVEVE' && (
                <Button
                  onClick={() => handleStatusChange(WorksheetStatus.FOLYAMATBAN)}
                  disabled={isMutating || shouldShowPaymentGate(worksheet)}
                  title={
                    shouldShowPaymentGate(worksheet)
                      ? t('worksheet.paymentGate.blockInfo')
                      : undefined
                  }
                >
                  {t('service.startWork')}
                </Button>
              )}
              {worksheet.status === 'FOLYAMATBAN' && (
                <>
                  <Button
                    onClick={() => handleStatusChange(WorksheetStatus.KESZ)}
                    disabled={isMutating || hasPendingParts}
                    title={hasPendingParts ? t('worksheet.pendingPartsBlock.blockInfo') : undefined}
                  >
                    {t('worksheet.markReadyAction')}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-950/20"
                    onClick={() => handleStatusChange(WorksheetStatus.FELFUGGESZTVE)}
                    disabled={isMutating}
                  >
                    {t('worksheet.suspendAction')}
                  </Button>
                </>
              )}
              {worksheet.status === 'FELFUGGESZTVE' && (
                <>
                  <Button
                    onClick={() => handleStatusChange(WorksheetStatus.FOLYAMATBAN)}
                    disabled={isMutating}
                  >
                    {t('worksheet.resumeAction')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => void handleStatusChange(WorksheetStatus.LEZART)}
                    disabled={isMutating || hasPendingParts}
                    title={hasPendingParts ? t('worksheet.pendingPartsBlock.blockInfo') : undefined}
                  >
                    {t('service.close')}
                  </Button>
                </>
              )}
              {(worksheet.status === 'FELVEVE' ||
                worksheet.status === 'FOLYAMATBAN' ||
                worksheet.status === 'VARHATO') && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/worksheet/${worksheet.id}/edit`)}
                >
                  {t('common.edit')}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/quotations/new?worksheetId=${worksheet.id}`)}
              >
                {t('service.quote')}
              </Button>
              {/* SRV-FG-5-1: Disassembly button (only for COMPLETED/KESZ/LEZART worksheets) */}
              {['KESZ', 'LEZART', 'COMPLETED'].includes(worksheet.status) && (
                <Button
                  variant="outline"
                  className="border-amber-500 text-amber-600 dark:text-amber-400"
                  onClick={() => setShowDisassembly(true)}
                >
                  {t('service.disassembly.button')}
                </Button>
              )}
              {/* SRV-FG-5-3: External forward button */}
              <ExternalForwardSection
                worksheetId={worksheet.id}
                isEditable={!['LEZART', 'TOROLVE', 'SZAMLAZANDO'].includes(worksheet.status)}
                onRefresh={refetch}
              />
              <Button variant="outline" onClick={() => setShowPrintPreview(true)}>
                {t('common.print')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Lifecycle state stepper (worksheet/02 v3-parity, additive) */}
        <WorksheetStateStepper status={worksheet.status as WorksheetStatus} className="mb-6" />

        {/* SRV-F1-1: Payment Gate banner */}
        {shouldShowPaymentGate(worksheet) && (
          <PaymentGateBanner worksheet={worksheet} onPaid={refetch} />
        )}
        {hasPendingParts &&
          (worksheet.status === 'FOLYAMATBAN' || worksheet.status === 'FELFUGGESZTVE') && (
            <div
              data-testid="pending-parts-block-banner"
              className="mb-4 rounded-md border border-orange-400 bg-orange-50 p-4 dark:border-orange-700 dark:bg-orange-950/30"
            >
              <p className="font-semibold text-orange-800 dark:text-orange-300">
                {t('worksheet.pendingPartsBlock.title')}
              </p>
              <p className="mt-1 text-sm text-orange-700 dark:text-orange-400">
                {t('worksheet.pendingPartsBlock.description', { count: pendingProcurement.length })}
              </p>
            </div>
          )}

        {/* SRV-FG-2-1 + SRV-F1-4: SUSPENDED banner with quote action */}
        {worksheet.status === 'FELFUGGESZTVE' && (
          <div className="mb-6 rounded-lg border border-orange-400 dark:border-orange-600 bg-orange-50 dark:bg-orange-950/20 p-4">
            <div className="flex items-start gap-3">
              <svg
                className="h-5 w-5 mt-0.5 text-orange-600 dark:text-orange-400 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-orange-800 dark:text-orange-300">
                  {t('worksheet.suspendedTitle')}
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-400 mt-1">
                  {t('worksheet.suspendedDescription')}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() =>
                      navigate(
                        `/quotations/new?worksheetId=${worksheet.id}&version=new&reason=additional_cost`
                      )
                    }
                  >
                    {t('worksheet.newQuoteVersion')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-orange-400 text-orange-700 dark:text-orange-300"
                    onClick={() => handleStatusChange(WorksheetStatus.FOLYAMATBAN)}
                    disabled={isMutating}
                  >
                    {t('worksheet.resumeOriginalScope')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {detailTabsEnabled ? tabbedLayout : legacyGrid}
      </div>

      {/* Story 58-8: Print preview modal */}
      <WorksheetPrintPreview
        open={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        worksheetId={worksheet.id}
        worksheetNumber={worksheet.worksheetNumber}
      />

      {/* SRV-FG-5-1: Disassembly modal */}
      <DisassemblyModal
        worksheetId={worksheet.id}
        isOpen={showDisassembly}
        onClose={() => setShowDisassembly(false)}
        onSuccess={() => {
          refetch();
          void fetchItems();
        }}
      />

      {/* PV-SZERVIZ-002: a korábbi "Mégis lezárom" confirm-warning-ot a lezárás-tiltás
          hard-gate (disabled-gomb + tooltip + banner) váltotta fel — függő alkatrész-beszerzéssel
          a munkalap nem fejezhető be / zárható le (a BE-422 a SSoT, a FE-disabled az UX). */}
    </div>
  );
}
